/**
 * 오픈플로우 영속화 — QBase 메타 등록 API(/qbase/openflow/*) 위에서 프로젝트/워크플로/캔버스를 읽고 쓴다.
 *
 * 규칙 (검토서 §8):
 *  - 저장 행의 키는 반드시 UPPER_SNAKE_CASE — camelCase 키는 백엔드가 에러 없이 조용히 무시한다.
 *  - 조회 결과의 키는 camelCase (openflow_project_id → openflowProjectId).
 *  - 캔버스는 OPENFLOW_WORKFLOW_VER 에 버전 행을 "추가만" 한다 (되돌리기 공짜). 헤더의 CUR_VER_NO 가 최신을 가리킨다.
 *  - 삭제는 소프트 삭제(USE_YN='N'). deletedRows 는 쓰지 않는다.
 *  - 식별자 OPENFLOW_WORKFLOW_VER 는 idc01+idc02(워크플로ID+버전) 복합 — 두 키를 항상 함께 넘긴다.
 */
import { Node, Edge } from '@xyflow/react';
import { migrateCanvas, stampSchema, toCanvasDoc, CanvasDoc } from './canvasSchema';
import { QBASE_SYS_ID, SEQ_DOMAIN, OPENFLOW_API, QUERY_PARAM } from '../config/qbase';
import { qbaseSelect, qbasePost, nextId } from './qbaseClient';
import type { Project, Workflow } from '../store/project/projectSlice';
import i18n from '../i18n';

// ---------------------------------------------------------------- 행 ↔ 모델

interface ProjectRow {
  openflowProjectId: string; projectNm: string; projectDc?: string | null;
  finalWorkflowId?: string | null; ownerUsrid?: string; useYn: string;
}
interface WorkflowRow {
  openflowWorkflowId: string; openflowProjectId: string; workflowNm: string;
  workflowDc?: string | null; iconCn?: string | null; curVerNo: string; useYn: string;
  /** 폴더경로 — 값이 NULL 인 행은 MyBatis 가 키 자체를 빼고 준다(키 없음 = 루트) */
  folderPath?: string | null;
  frstRegDt?: string | null; lastChgDt?: string | null;
}
interface WorkflowVerRow {
  openflowWorkflowId: string; verNo: string; canvasCn: string; regUsrid?: string;
}

export type { CanvasDoc } from './canvasSchema';

const toProject = (r: ProjectRow): Project => ({
  id: r.openflowProjectId,
  name: r.projectNm,
  description: r.projectDc || undefined,
  finalWorkflowId: r.finalWorkflowId || undefined,
});

const toWorkflow = (r: WorkflowRow): Workflow => ({
  id: r.openflowWorkflowId,
  projectId: r.openflowProjectId,
  name: r.workflowNm,
  description: r.workflowDc || undefined,
  icon: r.iconCn || undefined,
  folder: r.folderPath || undefined,
  curVerNo: Number(r.curVerNo || 0),
  createdAt: r.frstRegDt || undefined,
  updatedAt: r.lastChgDt || r.frstRegDt || undefined,
  // nodes/edges 는 목록에 싣지 않는다 — 캔버스를 열 때 loadCanvas 로 버전 행을 읽는다
});

// ---------------------------------------------------------------- 조회

export async function listProjects(): Promise<Project[]> {
  const rows = await qbaseSelect<ProjectRow>(OPENFLOW_API.PROJECTS);
  return rows.map(toProject);
}

export async function listWorkflows(projectId: string): Promise<Workflow[]> {
  const rows = await qbaseSelect<WorkflowRow>(OPENFLOW_API.WORKFLOWS, { [QUERY_PARAM.PROJECT_ID]: projectId });
  return rows.map(toWorkflow);
}

/** 캔버스 1버전 — 없으면 null (아직 한 번도 저장하지 않은 워크플로) */
export async function loadCanvas(workflowId: string, verNo: number): Promise<CanvasDoc | null> {
  if (!verNo || verNo < 1) return null;
  const rows = await qbaseSelect<WorkflowVerRow>(OPENFLOW_API.WORKFLOW_VER, {
    [QUERY_PARAM.WORKFLOW_ID]: workflowId,
    [QUERY_PARAM.VER_NO]: String(verNo),
  });
  if (rows.length === 0) return null;
  // 본문이 깨져 있으면 원시 SyntaxError 가 그대로 사용자 alert 에 노출됐다 — 무엇이 잘못됐는지 알려 준다
  let parsed: any;
  try {
    parsed = JSON.parse(rows[0].canvasCn || '{}');
  } catch (e) {
    throw new Error(i18n.t('common:error.canvasCorrupt', { ver: verNo }));
  }
  return toCanvasDoc(migrateCanvas(parsed));
}

export interface CanvasVersionInfo { verNo: number; regUsrid?: string; regDt?: string }

/** 버전 목록(최신순) — 버전 이력/복구 다이얼로그용. 본문(canvasCn)은 싣지 않고 메타만 추린다 */
export async function listCanvasVersions(workflowId: string): Promise<CanvasVersionInfo[]> {
  const rows = await qbaseSelect<WorkflowVerRow>(OPENFLOW_API.WORKFLOW_VER, {
    [QUERY_PARAM.WORKFLOW_ID]: workflowId,
  });
  return rows
    .map((r) => ({
      verNo: Number(r.verNo || 0),
      regUsrid: r.regUsrid,
      regDt: (r as any).frstRegDt || (r as any).regDt || (r as any).frstRegDtChar || '',
    }))
    .filter((v) => v.verNo > 0)
    .sort((x, y) => y.verNo - x.verNo);
}

// ---------------------------------------------------------------- 저장

export async function createProject(input: { name: string; description?: string }, ownerUsrid: string): Promise<Project> {
  const id = await nextId(SEQ_DOMAIN.PROJECT);
  await qbasePost(OPENFLOW_API.SAVE_PROJECT, {
    createdRows: [{
      OPENFLOW_PROJECT_ID: id,
      QBASE_SYS_ID: QBASE_SYS_ID,
      PROJECT_NM: input.name,
      PROJECT_DC: input.description ?? '',
      OWNER_USRID: ownerUsrid,
      USE_YN: 'Y',
    }],
    updatedRows: [], deletedRows: [],
  });
  return { id, name: input.name, description: input.description };
}

export async function updateProject(p: Project): Promise<void> {
  await qbasePost(OPENFLOW_API.SAVE_PROJECT, {
    createdRows: [],
    updatedRows: [{
      OPENFLOW_PROJECT_ID: p.id,
      PROJECT_NM: p.name,
      PROJECT_DC: p.description ?? '',
      FINAL_WORKFLOW_ID: p.finalWorkflowId ?? '',
    }],
    deletedRows: [],
  });
}

export async function createWorkflow(input: { projectId: string; name: string; description?: string; icon?: string; folder?: string }): Promise<Workflow> {
  const id = await nextId(SEQ_DOMAIN.WORKFLOW);
  await qbasePost(OPENFLOW_API.SAVE_WORKFLOW, {
    createdRows: [{
      OPENFLOW_WORKFLOW_ID: id,
      QBASE_SYS_ID: QBASE_SYS_ID,
      OPENFLOW_PROJECT_ID: input.projectId,
      WORKFLOW_NM: input.name,
      WORKFLOW_DC: input.description ?? '',
      ICON_CN: input.icon ?? '📄',
      CUR_VER_NO: '0',
      USE_YN: 'Y',
      FOLDER_PATH: input.folder ?? '',
    }],
    updatedRows: [], deletedRows: [],
  });
  return { id, projectId: input.projectId, name: input.name, description: input.description, icon: input.icon ?? '📄', folder: input.folder || undefined, curVerNo: 0 };
}

export async function updateWorkflowMeta(w: { id: string; name: string; description?: string; folder?: string }): Promise<void> {
  await qbasePost(OPENFLOW_API.SAVE_WORKFLOW, {
    createdRows: [],
    updatedRows: [{
      OPENFLOW_WORKFLOW_ID: w.id, WORKFLOW_NM: w.name, WORKFLOW_DC: w.description ?? '',
      // folder 를 주지 않으면 건드리지 않는다 ('' 은 루트로 이동)
      ...(w.folder !== undefined ? { FOLDER_PATH: w.folder } : {}),
    }],
    deletedRows: [],
  });
}

/** 소프트 삭제 — 물리 삭제 금지 */
export async function retireWorkflow(workflowId: string): Promise<void> {
  await qbasePost(OPENFLOW_API.SAVE_WORKFLOW, {
    createdRows: [],
    updatedRows: [{ OPENFLOW_WORKFLOW_ID: workflowId, USE_YN: 'N' }],
    deletedRows: [],
  });
}

/**
 * 캔버스 저장 = 새 버전 행 INSERT + 헤더 CUR_VER_NO 갱신. 두 호출이라 원자적이지 않다 —
 * 버전 행이 들어가고 헤더 갱신이 실패하면 "고아 버전" 이 남지만 데이터는 잃지 않는다(헤더는 이전 버전을 가리킴).
 * 반환: 저장된 버전 번호.
 */
export async function saveCanvas(workflowId: string, curVerNo: number, doc: CanvasDoc, regUsrid: string): Promise<number> {
  const nextVer = (curVerNo || 0) + 1;
  await qbasePost(OPENFLOW_API.SAVE_WORKFLOW_VER, {
    createdRows: [{
      OPENFLOW_WORKFLOW_ID: workflowId,
      VER_NO: String(nextVer),
      QBASE_SYS_ID: QBASE_SYS_ID,
      CANVAS_CN: JSON.stringify(stampSchema({ nodes: doc.nodes, edges: doc.edges, settings: doc.settings })),
      REG_USRID: regUsrid,
    }],
    updatedRows: [], deletedRows: [],
  });
  await qbasePost(OPENFLOW_API.SAVE_WORKFLOW, {
    createdRows: [],
    updatedRows: [{ OPENFLOW_WORKFLOW_ID: workflowId, CUR_VER_NO: String(nextVer) }],
    deletedRows: [],
  });
  return nextVer;
}
